import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type { SetAppUserBannedResult } from 'src/types/user';
import type {
  AppUsersState,
  SuggestedUser,
  AppUsersPayload,
  AppUsersResponse,
  SuggestedUsersResponse,
} from 'src/store/types/app-users-types';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

export const initialState: AppUsersState = {
  list: {
    users: [],
    status: 'idle',
    error: '',
  },
  suggested: {
    users: [],
    status: 'idle',
    error: '',
  },
};

// ====== Thunks (typed) ======
export const loadAppUsers = createAsyncThunk<AppUsersPayload, void>(
  'app-users/loadAppUsers',
  async () => {
    const res = await api.get<AppUsersResponse>(endpoints.admin.appUsers.appUsers);
    return res.data.data; // <-- AppUsersPayload
  }
);

export const loadSuggestedAppUsers = createAsyncThunk<SuggestedUser[], void>(
  'app-users/loadSuggestedAppUsers',
  async () => {
    const response = await api.get<SuggestedUsersResponse>(endpoints.admin.appUsers.suggested);
    return response.data.suggestedUsers || [];
  }
);

export const updateSuggestedAppUsers = createAsyncThunk<SuggestedUser[], string[]>(
  'app-users/updateSuggestedAppUsers',
  async (userIds) => {
    const response = await api.put<SuggestedUsersResponse>(endpoints.admin.appUsers.suggested, {
      userIds,
    });
    return response.data.suggestedUsers || [];
  }
);

export const updateAppUserCertified = createAsyncThunk<
  { id: string; certified: boolean },
  { userId: string; certified: boolean }
>('app-users/updateAppUserCertified', async ({ userId, certified }) => {
  const response = await api.patch<{ id: string; certified: boolean }>(
    `${endpoints.admin.appUsers.appUsers}/${userId}/certified`,
    { certified }
  );
  return response.data;
});

/**
 * KAN-333: suspend/reinstate a reported user.
 *
 * KAN-660: the sanction now has a kind. `durationDays` present is a temporary
 * SUSPENSION — the app shows « Votre compte est suspendu » and promises an
 * email back — and absent is a DEFINITIVE closure, which promises nothing.
 * Omitting it on a reinstatement is required, not merely tidy: the server
 * rejects a duration there as a contradiction.
 */
export const setAppUserBanned = createAsyncThunk<
  SetAppUserBannedResult,
  { userId: string; banned: boolean; durationDays?: number }
>('app-users/setAppUserBanned', async ({ userId, banned, durationDays }) => {
  const response = await api.patch<SetAppUserBannedResult>(
    `${endpoints.admin.appUsers.appUsers}/${userId}/ban`,
    banned && durationDays !== undefined ? { banned, durationDays } : { banned }
  );
  return response.data;
});

// KAN-333: hard-delete a reported user (backend cascades their content).
export const deleteAppUser = createAsyncThunk<string, string>(
  'app-users/deleteAppUser',
  async (userId) => {
    await api.delete(`${endpoints.admin.appUsers.appUsers}/${userId}`);
    return userId;
  }
);

// ...

const slice = createSlice({
  name: 'app-users',
  initialState,
  reducers: {
    setAppUsers(appUsersState: Draft<AppUsersState>, action: PayloadAction<{ appUsers: any[] }>) {
      appUsersState.list = {
        users: action.payload.appUsers,
        status: 'succeeded',
        error: '',
      };
    },
    setSuggestedAppUsers(
      appUsersState: Draft<AppUsersState>,
      action: PayloadAction<SuggestedUser[]>
    ) {
      appUsersState.suggested = {
        users: action.payload,
        status: 'succeeded',
        error: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAppUsers.pending, (state: Draft<AppUsersState>) => {
        state.list.status = 'loading';
        state.list.error = '';
      })
      .addCase(loadAppUsers.fulfilled, (state: Draft<AppUsersState>, action) => {
        console.log('action.payload:', action.payload);
        state.list.status = 'succeeded';
        state.list.users = action.payload.appUsers; // <-- no cast needed
      })
      .addCase(loadAppUsers.rejected, (state: Draft<AppUsersState>, action) => {
        state.list.status = 'failed';
        state.list.error = action.error?.message ?? 'Failed to load appUsers';
      })
      .addCase(loadSuggestedAppUsers.pending, (state: Draft<AppUsersState>) => {
        state.suggested.status = 'loading';
        state.suggested.error = '';
      })
      .addCase(loadSuggestedAppUsers.fulfilled, (state: Draft<AppUsersState>, action) => {
        state.suggested.status = 'succeeded';
        state.suggested.users = action.payload;
      })
      .addCase(loadSuggestedAppUsers.rejected, (state: Draft<AppUsersState>, action) => {
        state.suggested.status = 'failed';
        state.suggested.error = action.error?.message ?? 'Failed to load suggested appUsers';
      })
      .addCase(updateSuggestedAppUsers.pending, (state: Draft<AppUsersState>) => {
        state.suggested.status = 'loading';
        state.suggested.error = '';
      })
      .addCase(updateSuggestedAppUsers.fulfilled, (state: Draft<AppUsersState>, action) => {
        state.suggested.status = 'succeeded';
        state.suggested.users = action.payload;
      })
      .addCase(updateSuggestedAppUsers.rejected, (state: Draft<AppUsersState>, action) => {
        state.suggested.status = 'failed';
        state.suggested.error = action.error?.message ?? 'Failed to update suggested appUsers';
      })
      .addCase(updateAppUserCertified.fulfilled, (state: Draft<AppUsersState>, action) => {
        const { id, certified } = action.payload;
        const user = state.list.users.find((u) => u.id === id);
        if (user) {
          user.certified = certified;
        }
      })
      /*
        The ban thunk had no fulfilled case, so the request succeeded and the
        table never moved: the row kept its old status until a full reload, and
        the "Banned" tab kept its old count. Both `banned` and `status` are
        written because the row menu branches on the flag while the Status cell
        and the tab filters read the label — the server derives one from the
        other, so keeping them in step here is what makes the list agree with it.
      */
      .addCase(setAppUserBanned.fulfilled, (state: Draft<AppUsersState>, action) => {
        const { id, banned } = action.payload;
        const user = state.list.users.find((u) => u.id === id);
        if (user) {
          user.banned = banned;
          user.status = banned ? 'banned' : 'active';
        }
      })
      .addCase(deleteAppUser.fulfilled, (state: Draft<AppUsersState>, action) => {
        state.list.users = state.list.users.filter((u) => u.id !== action.payload);
      });
  },
});

export const { setAppUsers, setSuggestedAppUsers } = slice.actions;
export default slice.reducer;

export const selectAppUsersList = (s: RootState) => s.appUser.list.users;
export const selectSuggestedAppUsersList = (s: RootState) => s.appUser.suggested.users;
export const selectSuggestedAppUsersStatus = (s: RootState) => s.appUser.suggested.status;
